import {
  ShiftManagementEnumsRestDayStateEnum,
  ShiftsRestDayFragment,
} from 'generated/resources/graphql'
import { partition, uniq } from 'lodash'
import { computed, makeObservable } from 'mobx'

import { stringToDate } from 'modules/core/lib/dates'

import RestDay, { Props } from 'modules/shift_management/store/internal/models/RestDay'

interface RestDayPayload {
  date: string
  employee_id: number
  id: number
  state: ShiftManagementEnumsRestDayStateEnum
}

class RestDays {
  restDays: RestDay[]

  constructor(restDays: RestDay[]) {
    this.restDays = restDays

    makeObservable(this, {
      employeeIds: computed,
      restDaysIds: computed,
      restDaysLength: computed,
    })
  }

  /**
   * Transforms an array of objects from a request into an array of RestDay
   * objects
   */
  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromProps(restDays: RestDayPayload[]): RestDays {
    const restDayObjects = restDays.map((restDay) => {
      return new RestDay({
        id: restDay.id,
        employeeId: restDay.employee_id,
        state: restDay.state,
        date: stringToDate(restDay.date, 'backend_datetime'),
      })
    })

    return new RestDays(restDayObjects)
  }

  static buildFromGraphQL(fragments: ShiftsRestDayFragment[]): RestDays {
    const restDayObjects = fragments.map((fragment) => {
      return new RestDay({
        id: fragment.id,
        // TODO:
        employeeId: fragment.employeeId,
        state: fragment.state,
        date: stringToDate(fragment.date, 'backend_datetime'),
        fragment: fragment,
      })
    })

    return new RestDays(restDayObjects)
  }

  get restDaysLength(): number {
    return this.restDays.length
  }

  filterByDate(date: moment.Moment): RestDay[] {
    return this.restDays.filter((restDay) => restDay.isDate(date))
  }

  filterByRange(firstDay: moment.Moment, lastDay: moment.Moment): RestDays {
    const restDays = this.restDays.filter((restDay: RestDay) => restDay.inRange(firstDay, lastDay))

    return new RestDays(restDays)
  }

  get restDaysIds(): number[] {
    return this.restDays.map((s) => s.id)
  }

  get employeeIds(): number[] {
    return uniq(this.restDays.map((s) => s.employeeId))
  }

  addRestDay(props: Props): void {
    this.restDays.push(new RestDay({ ...props }))
  }

  addRestDays(days: RestDay[]): void {
    this.restDays.push(...days)
  }

  destroyRestDay(id: number): void {
    const index = this.restDays.findIndex((restDay) => restDay.id === id)
    if (index >= 0) this.restDays.splice(index, 1)
  }

  destroyRestDays(ids: number[]): void {
    const idsSet = new Set(ids)
    const [restDaysToDestroy, restDaysToAdd] = partition(this.restDays, (r: RestDay) =>
      idsSet.has(r.id)
    )

    if (restDaysToDestroy.length === ids.length) {
      this.restDays = restDaysToAdd
    }
  }

  filterByDraft(): RestDay[] {
    return this.restDays.filter((day: RestDay) => day.isDraft)
  }

  filterByPublished(): RestDay[] {
    return this.restDays.filter((day: RestDay) => day.isPublished)
  }

  filterPublishedByDate(date: moment.Moment): RestDay[] {
    return this.restDays.filter((day: RestDay) => day.isPublished && day.isDate(date))
  }
}

export default RestDays
